import { DatePicker, Form, Input, message, Select } from 'antd';
import { insuredApi } from 'services/apis';
import { SortedSelect } from 'components/ui/sorted-select';
import CalendarIcon from 'assets/icons/calendar';
import {
  fields,
  getValidationSchema,
  initialValues,
} from 'components/features/insured-form/form';
import { AddressForm } from 'components/ui/address-form';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';
import FormActionButtons from 'components/ui/form-action-buttons';
import FormContainer from 'components/ui/form-container';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormSection from 'components/ui/form-section';
import StatusIndicator from 'components/ui/status-indicator';
import { ENavigationPath } from 'constants/path';
import {
  CreateInsuredPayloadDto,
  CreateInsuredPayloadDtoStatusEnum,
} from 'libs/pinnacle-sdk';
import { debounce, isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router';
import { handleError, sanitizeFormValue, validateForm } from 'utils/form';
import { AddressFormInitialData } from 'components/ui/address-form/types';
import { useSubmissionFormStore } from 'stores/submission-form';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
const { TextArea } = Input;

interface InsuredFormProps {
  mode?: 'create' | 'update';
  initialData?: object;
  onSubmit?: (data: CreateInsuredPayloadDto) => Promise<void>;
  isLoading?: boolean;
}

const InsuredForm = ({
  mode = 'create',
  initialData = initialValues,
  onSubmit,
  isLoading = false,
}: InsuredFormProps) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { updateFormData } = useSubmissionFormStore();

  const createInsuredMutation = useMutation(
    (insured: CreateInsuredPayloadDto) =>
      insuredApi.createInsured({
        createInsuredPayloadDto: insured,
      }),
    {
      onSuccess: (response) => {
        if (location.state?.from === ENavigationPath.NEW_SUBMISSION) {
          updateFormData({
            insuredId: response.insuredId,
            insuredName: response.name,
          });
        }
        message.success(
          t('forms.createRecordSuccess', { type: t('insured.insured') })
        );
        resetAndNavigate();
      },
      onError: async (error: { response: Response }) =>
        handleError({
          error,
          defaultMessage: t('forms.createRecordFail', {
            type: t('insured.insured'),
          }),
        }),
    }
  );
  const [form] = Form.useForm<CreateInsuredPayloadDto>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Initialize form with initialData when available
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData);
      // Validate form after setting initial values
      validateForm<CreateInsuredPayloadDto>(
        initialData as CreateInsuredPayloadDto,
        form,
        fields,
        getValidationSchema(t)
      ).then(setIsFormValid);
    }
  }, [form, initialData, t]);

  const resetAndNavigate = useCallback(() => {
    form.resetFields();
    setShowConfirmation(false);
    if (location.state?.from === ENavigationPath.NEW_SUBMISSION) {
      return navigate(ENavigationPath.NEW_SUBMISSION, {
        state: {
          from: ENavigationPath.NEW_INSURED,
        },
      });
    }

    return navigate(ENavigationPath.INSURED_RECORDS);
  }, [form, location.state, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpsertInsuredMutate = useCallback(
    debounce((insured: CreateInsuredPayloadDto) => {
      if (mode === 'update' && onSubmit) {
        onSubmit(insured)
          .then(() => {
            message.success(
              t('forms.updateRecordSuccess', { type: t('insured.insured') })
            );
            navigate(ENavigationPath.INSURED_RECORDS);
          })
          .catch((error) => {
            handleError({
              error,
              defaultMessage: t('forms.updateRecordFail', {
                type: t('insured.insured'),
              }),
            });
          });
      } else {
        createInsuredMutation.mutate(insured);
      }
    }, 500),
    [createInsuredMutation, mode, onSubmit, navigate, t]
  );

  return (
    <FormContainer
      title={
        mode === 'create'
          ? t('insured.setupNewInsured')
          : t('insured.updateInsured')
      }
    >
      <Form
        form={form}
        layout="vertical"
        className="space-y-8"
        onValuesChange={async () => {
          const values = form.getFieldsValue();
          const isValid = await validateForm<CreateInsuredPayloadDto>(
            values,
            form,
            fields,
            getValidationSchema(t)
          );
          setIsFormValid(isValid);
        }}
        initialValues={initialValues}
        onFinish={async (insured: CreateInsuredPayloadDto) => {
          const sanitizedInsured = sanitizeFormValue(insured);
          debouncedUpsertInsuredMutate(sanitizedInsured);
        }}
      >
        <FormSection title={t('insured.insuredDetails')}>
          <FormLayoutGroup>
            <Form.Item name="status" label={t('general.status')}>
              <SortedSelect
                allowClear
                placeholder={t('general.selectField', {
                  field: t('general.status'),
                })}
                options={Object.values(CreateInsuredPayloadDtoStatusEnum).map(
                  (status) => ({ label: status, value: status })
                )}
                optionRender={(option) => (
                  <StatusIndicator
                    status={option.value as CreateInsuredPayloadDtoStatusEnum}
                  />
                )}
                labelRender={(props) => (
                  <StatusIndicator
                    /* eslint-disable-next-line react/prop-types */
                    status={props.value as CreateInsuredPayloadDtoStatusEnum}
                  />
                )}
              ></SortedSelect>
            </Form.Item>
            <Form.Item name="name" label={t('insured.insuredName')}>
              <Input size="large" placeholder={t('insured.enterInsuredName')} />
            </Form.Item>
          </FormLayoutGroup>
          <FormLayoutGroup mdColumns={1}>
            <Form.Item name="longName" label={t('insured.insuredLongName')}>
              <Input
                size="large"
                placeholder={t('insured.enterInsuredLongName')}
              />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>
        <FormSection title={t('general.address')}>
          <AddressForm
            resetFormField={(fieldName: string) =>
              form.setFieldsValue({ [fieldName]: undefined })
            }
            initialData={initialData as AddressFormInitialData}
          />
        </FormSection>

        <FormSection title="Sanctions">
          <FormLayoutGroup>
            <Form.Item
              name="sanctionsCompleted"
              label={t('insured.sanctionsCompleted') + '?'}
            >
              <Select
                allowClear
                size="large"
                placeholder={t('general.select')}
                options={[
                  { label: t('general.yes'), value: true },
                  { label: t('general.no'), value: false },
                ]}
                suffixIcon={<ArrowDownIcon />}
              ></Select>
            </Form.Item>

            <Form.Item
              name="sanctionsCompletedDate"
              label={t('insured.sanctionsCompletionDate')}
            >
              <DatePicker
                format={'DD/MM/YYYY'}
                allowClear
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item name="comment" label={t('general.comments')}>
              <TextArea
                rows={4}
                placeholder={t('general.enter', {
                  field: t('general.comments'),
                })}
              ></TextArea>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormActionButtons
          onCancel={() => {
            const currentValue = form.getFieldsValue();

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { createdAt, updatedAt, insuredId, ...compareValues } =
              initialData as unknown as {
                createdAt?: Date;
                updatedAt?: Date;
                insuredId?: string;
                [key: string]: unknown;
              };

            if (isEqual(currentValue, compareValues)) return resetAndNavigate();

            return setShowConfirmation(true);
          }}
          isConfirmDisabled={
            !isFormValid || isLoading || createInsuredMutation.isLoading
          }
          confirmText={
            mode === 'create' ? t('insured.completeInsured') : t('general.save')
          }
        />
        <ConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={resetAndNavigate}
          title={t('forms.discardChangesTitle')}
          content={t('forms.discardChangesContent', {
            type: t('insured.insured'),
          })}
          okText={t('forms.discardChangesOkText')}
          cancelText={t('forms.discardChangesCancelText')}
          type={ConfirmationModalType.DANGER}
        />
      </Form>
    </FormContainer>
  );
};

export default InsuredForm;
